import React, { useState, useEffect } from 'react';
import Button from '../../../components/Button';

/**
 * Chat component for collecting user information via question/answer format
 */
const InformationCollectionChat = ({ 
  substeps, 
  stageId, 
  onInputChange, 
  onSubmitAnswer, 
  formData,
  isSubstepCompleted,
  onNextStage,
  storeUrl
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [slideDirection, setSlideDirection] = useState('right');
  
  // On initial load, ensure starting from the first incomplete question
  useEffect(() => {
    if (substeps && substeps.length > 0) {
      let firstUnansweredIndex = 0;
      
      // Find the first unanswered question
      for (let i = 0; i < substeps.length; i++) {
        if (!isSubstepCompleted(substeps[i].id)) {
          firstUnansweredIndex = i;
          break;
        }
      }
      
      setCurrentQuestion(firstUnansweredIndex);
      
      // If there is saved temporary data, load it into the input field
      const currentSubstep = substeps[firstUnansweredIndex];
      if (currentSubstep && formData?.[currentSubstep.id]) {
        const savedValue = formData[currentSubstep.id];
        setUserInput(savedValue);
        // Special handling if the saved value is credentials
        if (currentSubstep.followUp?.type === 'credentials' && savedValue === 'Yes') {
          // Assuming credentials are saved in a separate structure or need parsing
          // This part might need adjustment based on how credentials are stored/retrieved
          // For simplicity, let's assume they are not directly pre-filled here but handled in renderInput
        }
      }
    }
  }, [substeps, formData, isSubstepCompleted]);

  // Handle input value changes
  const handleInputChange = (value) => {
    const currentQ = substeps[currentQuestion];
    setUserInput(value);
    
    // Save temporary data
    if (currentQ) {
      onInputChange(currentQ.id, value);
    }
  };

  // Handle option selection (for choice questions)
  const handleOptionSelect = (option) => {
    const currentQ = substeps[currentQuestion];
    
    // Always update the input state and save temporarily
    handleInputChange(option); 

    // If there's no follow-up or the selected option doesn't trigger a follow-up,
    // and it's a simple choice type, submit automatically.
    if (currentQ && (currentQ.type === 'boolean' || currentQ.type === 'select') && 
        (!currentQ.followUp || option !== currentQ.followUp.condition)) {
      handleSubmit(null, option);
    }
  };

  // Handle multi-selection
  const handleMultiSelect = (option) => {
    const currentQ = substeps[currentQuestion];
    const selectedOptions = userInput && typeof userInput === 'string' ? userInput.split(',') : [];
    let newSelected;
    
    if (selectedOptions.includes(option)) {
      newSelected = selectedOptions.filter(item => item !== option);
    } else {
      newSelected = [...selectedOptions, option];
    }
    
    // Update input and save temporary data
    handleInputChange(newSelected.join(','));
    
    // For multiselect, we usually require a manual submit, so no auto-submit here.
  };

  // Submit the answer
  const handleSubmit = async (e, directValue = null) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const currentQ = substeps[currentQuestion];
    if (!currentQ) return;
    
    let valueToSubmit = directValue !== null ? directValue : userInput;

    // Special handling for credentials follow-up
    if (currentQ.followUp?.type === 'credentials' && userInput === 'Yes') {
      if (!credentials.username || !credentials.password) {
        alert('Please provide both username and password for app access.'); // Simple validation
        return; 
      }
      // Save credentials as a JSON string or structured object
      valueToSubmit = JSON.stringify(credentials); 
    }

    // Basic validation: Check if input is empty for non-optional questions
    if (!valueToSubmit && !currentQ.optional) { 
        alert('This field is required.'); // Simple validation
        return; 
    }
    
    setIsSubmitting(true);
    
    try {
      // Save the answer to Firebase
      const success = await onSubmitAnswer(stageId, currentQ.id, valueToSubmit);
      
      if (success) {
        // Check if we are in a follow-up state and should remain on the same question
        if (currentQ.followUp && valueToSubmit === currentQ.followUp.condition && currentQ.followUp.type === 'credentials') {
          // If the condition for follow-up (like selecting 'Yes') is met AND it's credentials,
          // we just submitted 'Yes'. Stay on the question to fill credentials.
          // No state change needed here, just don't advance.
           setIsSubmitting(false); // Allow credential input
           return; // Stay on current question
        }

        // If submission was successful and no follow-up condition keeps us here, advance.
        if (currentQuestion < substeps.length - 1) {
          // Animation for transitioning to the next question
          setSlideDirection('left');
          await new Promise(resolve => setTimeout(resolve, 300)); // Wait for animation
          setCurrentQuestion(prev => prev + 1);
          setSlideDirection('right'); // Prepare for next potential transition
          
          // Clear input field and credential state for the next question
          setUserInput('');
          setCredentials({ username: '', password: '' });
          
          // Pre-fill next question's input if temporary data exists
          const nextQuestion = substeps[currentQuestion + 1];
          if (nextQuestion && formData?.[nextQuestion.id]) {
            setUserInput(formData[nextQuestion.id]);
          }

        } else {
          // All questions in this stage are completed
          console.log('All questions completed!');
          // Optionally call a stage completion handler here if needed
          
          // Show completion message instead of automatically moving to next stage
          setIsSubmitting(false);
          // Don't call onNextStage automatically - let user click Next button in UI
        }
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      alert('An error occurred while saving your answer. Please try again.'); // User feedback
    } finally {
      // Only set isSubmitting to false if we are NOT waiting for credential input
      if (!(currentQ.followUp?.type === 'credentials' && userInput === 'Yes')) {
          setIsSubmitting(false);
      }
    }
  };

  // Go to the previous question
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setSlideDirection('right'); // Animate current out to the right
      // Use timeout to allow animation before state change if desired, or just change state:
      setCurrentQuestion(prev => prev - 1);
      setSlideDirection('left'); // Prepare next animation direction
      
      // Load the temporary value of the previous question if it exists
      const prevQuestion = substeps[currentQuestion - 1];
      if (prevQuestion && formData?.[prevQuestion.id] !== undefined) {
        setUserInput(formData[prevQuestion.id]);
      } else {
        setUserInput(''); // Clear input if no temp data
      }
      // Reset credentials if going back to a non-credential question
       if (prevQuestion?.followUp?.type !== 'credentials') {
         setCredentials({ username: '', password: '' });
       }
    }
  };

  // Function to render the appropriate input field based on question type
  const renderInput = () => {
    const currentQ = substeps[currentQuestion];
    if (!currentQ) return null;
    
    switch (currentQ.type) {
      case 'boolean':
        // Ensure options are defined and are strings, default to Yes/No
        const booleanOptions = currentQ.options || ['Yes', 'No'];
        return (
          <div className="space-y-4">
            <div className="flex gap-3">
              {booleanOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => handleOptionSelect(option)}
                  className={`flex-1 py-2.5 px-4 rounded-xl transition-all duration-300 ${
                    userInput === option
                      ? 'bg-gradient-to-r from-primary to-primary-light text-white shadow-lg transform scale-105'
                      : 'bg-gray-800/50 text-white hover:bg-gray-700/50 border border-gray-700/30'
                  }`}
                >
                  {option} {/* Option text (Yes/No) */}
                </button>
              ))}
            </div>
            
            {/* Display follow-up fields if the condition is met */}
            {currentQ.followUp && userInput === currentQ.followUp.condition && (
              <div className="mt-4 space-y-3">
                <p className="text-sm text-gray-400">{currentQ.followUp.question || 'Please provide more details:'}</p>
                {/* Specific rendering for credentials follow-up */}
                {currentQ.followUp.type === 'credentials' && (
                  <div className="space-y-3">
                    {(currentQ.followUp.fields || []).map((field) => (
                      <div key={field.id} className="space-y-1">
                        <label className="text-xs text-gray-400">{field.label || field.id}</label>
                        <input
                          type={field.type || 'text'}
                          value={credentials[field.id] || ''}
                          onChange={(e) => setCredentials(prev => ({
                            ...prev,
                            [field.id]: e.target.value
                          }))}
                          placeholder={field.placeholder || `Enter ${field.label || field.id}`}
                          className="w-full bg-gray-800/50 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50 border border-gray-700/30 transition-all duration-300"
                          required // Add required attribute for better UX
                        />
                      </div>
                    ))}
                    {/* Add submit button specifically for credentials */}
                     <Button
                      onClick={handleSubmit} // Re-use handleSubmit to submit credentials
                      disabled={isSubmitting || !credentials.username || !credentials.password}
                      className={`w-full mt-2 py-2 rounded-lg text-sm ${isSubmitting ? 'bg-gray-600' : 'bg-primary hover:bg-primary-light'}`}
                    >
                      {isSubmitting ? 'Saving Credentials...' : 'Save Credentials and Continue'}
                    </Button>
                  </div>
                )}
                 {/* Add rendering for other follow-up types if needed */}
              </div>
            )}
          </div>
        );
      
      case 'select':
        return (
          <select
            value={userInput}
            onChange={(e) => {
              const value = e.target.value;
              // Only call handleOptionSelect if a valid option is chosen
              if (value) { 
                handleOptionSelect(value); 
              }
            }}
            className="w-full bg-gray-800/50 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50 border border-gray-700/30 transition-all duration-300"
          >
            <option value="">-- Select an option --</option> {/* Translated placeholder */}
            {(currentQ.options || []).map((option) => (
              <option key={option} value={option}>
                {option} {/* Option text */}
              </option>
            ))}
          </select>
        );
      
      case 'multiselect':
        const selectedOptions = typeof userInput === 'string' && userInput ? userInput.split(',') : [];
        return (
          <div className="grid grid-cols-2 gap-2">
            {(currentQ.options || []).map((option) => (
              <button
                key={option}
                onClick={() => handleMultiSelect(option)}
                className={`py-2 px-3 rounded-xl transition-all duration-300 text-sm ${
                  selectedOptions.includes(option)
                    ? 'bg-gradient-to-r from-primary to-primary-light text-white shadow-lg transform scale-105'
                    : 'bg-gray-800/50 text-white hover:bg-gray-700/50 border border-gray-700/30'
                }`}
              >
                {option} {/* Option text */}
              </button>
            ))}
          </div>
        );

      case 'textarea': // Added case for textarea
         return (
          <div className="space-y-2">
            <textarea
              value={userInput}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={currentQ.placeholder || 'Enter your response...'} // Translated
              rows={4} // Default rows, can be customized
              className="w-full bg-gray-800/50 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50 border border-gray-700/30 transition-all duration-300"
            />
            {currentQ.helpText && (
              <p className="mt-1 text-xs text-gray-400">{currentQ.helpText}</p> // Adjusted style
            )}
          </div>
        );
      
      default: // Handles text, email, password, etc.
        return (
          <div className="space-y-2">
            <input
              type={currentQ.type || 'text'}
              value={userInput}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={currentQ.placeholder || 'Type your answer...'} // Translated
              className="w-full bg-gray-800/50 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50 border border-gray-700/30 transition-all duration-300"
              required={!currentQ.optional} // Add required attribute for better UX
            />
            {currentQ.helpText && (
              <p className="mt-1 text-xs text-gray-400">{currentQ.helpText}</p> // Adjusted style
            )}
          </div>
        );
    }
  };

  // Render the current question UI
  const renderQuestion = () => {
    const currentQ = substeps[currentQuestion];
    if (!currentQ) return <p className="text-center text-gray-400">Loading question...</p>; // Handle loading state

    // Check if all questions are completed
    const allQuestionsCompleted = substeps.every(substep => isSubstepCompleted(substep.id));

    // If all questions are completed, show completion message
    if (allQuestionsCompleted) {
      return (
        <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 backdrop-blur-sm rounded-2xl p-6 border border-green-700/30 shadow-lg w-full">
          <div className="flex items-center gap-3 mb-4">
            {/* Checkmark icon */}
            <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <h3 className="text-lg font-semibold text-green-400">Closed Testing Stage Completed!</h3>
          </div>
          <p className="text-gray-300 mb-4">
            Updated: Congratulations! You have successfully completed all the questions for the closed testing stage. Your app is now being processed for the next stage.
          </p>
          <div className="mt-4">
            <a 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                window.open(storeUrl || "https://play.google.com/console", "_blank");
              }}
              className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              View your app in Google Play Console
            </a>
          </div>
          <p className="text-sm text-gray-400 mt-3">
            Use the "Next" button at the bottom of the screen to continue to the next stage when you're ready.
          </p>
        </div>
      );
    }

    // If the question is already completed, show feedback
    if (isSubstepCompleted(currentQ.id)) {
      return (
        <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 backdrop-blur-sm rounded-2xl p-6 border border-green-700/30 shadow-lg w-full">
          <div className="flex items-center gap-3 mb-4">
            {/* Checkmark icon */}
            <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <h3 className="text-lg font-semibold text-green-400">{currentQ.title} - Completed</h3> {/* Translated */}
          </div>
          <p className="text-gray-300 mb-4">{currentQ.question}</p>
          <p className="text-sm text-gray-400">
            This answer has been saved successfully. You can proceed to the next question.
          </p>
          <div className="flex justify-end mt-4">
            <button
              onClick={() => {
                // Advance only if not the last question
                if (currentQuestion < substeps.length - 1) {
                  // Animate and change question
                  setSlideDirection('left');
                  setTimeout(() => {
                      setCurrentQuestion(prev => prev + 1);
                      setSlideDirection('right'); // Prepare next animation
                      // Pre-fill next question
                      const nextQ = substeps[currentQuestion + 1];
                      setUserInput(formData?.[nextQ?.id] || '');
                  }, 300); 
                }
              }}
              className="px-4 py-2 bg-green-600/70 text-white rounded-lg hover:bg-green-500/70 transition-colors"
              disabled={currentQuestion >= substeps.length - 1} // Disable on last question
            >
              Next Question {/* Translated */}
            </button>
          </div>
        </div>
      );
    }

    // Render the active question form
    return (
      <form 
        onSubmit={handleSubmit} // Use form onSubmit
        className={`transform transition-all duration-300 ease-in-out h-full flex items-center justify-center ${ 
          slideDirection === 'right' // Adjust animation logic if needed
            ? 'animate-slide-in-right' // Example animation class
            : 'animate-slide-in-left' // Example animation class
        }`}
      >
        <div className="bg-gradient-to-br from-gray-800/80 to-gray-800/60 backdrop-blur-sm rounded-2xl p-4 border border-gray-700/50 shadow-lg w-full max-w-md">
          {/* Question Header */}
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
              <span className="text-primary text-sm font-semibold">{currentQuestion + 1}</span>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">{currentQ.title}</h3>
              <p className="text-xs text-gray-400">{currentQ.question}</p>
            </div>
          </div>
          
          {/* Input Area */}
          <div className="mt-2">
            {renderInput()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-3">
            <button
              type="button" // Prevent form submission
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className={`px-3 py-1.5 rounded-lg transition-all duration-300 text-xs ${
                currentQuestion === 0
                  ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                  : 'bg-gray-700 text-white hover:bg-gray-600'
              }`}
            >
              Previous {/* Translated */}
            </button>
            {/* Submit button - Type submit for form handling */}
            <Button 
                type="submit" 
                disabled={isSubmitting || (!userInput && !currentQ.optional && currentQ.followUp?.type !== 'credentials')} // Disable logic needs care
                className={`px-3 py-1.5 rounded-lg transition-all duration-300 text-xs ${isSubmitting ? 'opacity-50 cursor-wait' : ''}`} // Simplified disabled style
            >
                {isSubmitting ? (
                    <span className="flex items-center gap-1">
                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                    </span>
                ) : (
                    // Determine button text: 'Save Credentials' if applicable, else 'Finish' or 'Next'
                    (currentQ.followUp?.type === 'credentials' && userInput === 'Yes') 
                        ? 'Save Credentials' 
                        : (currentQuestion === substeps.length - 1 ? 'Finish' : 'Next')
                )}
            </Button>
          </div>
        </div>
      </form>
    );
  };

  // Progress Bar
  const renderProgressBar = () => (
    <div className="mb-4">
      <div className="text-[10px] text-gray-400 mb-0.5 flex justify-between">
        {/* Translated Progress Text */}
        <span>Question {currentQuestion + 1} of {substeps.length}</span>
        <span>{Math.round(((currentQuestion + 1) / substeps.length) * 100)}% Completed</span>
      </div>
      <div className="w-full h-1 bg-gray-800 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-primary to-primary-light transition-all duration-500 ease-out"
          style={{ width: `${((currentQuestion + 1) / substeps.length) * 100}%` }}
        ></div>
      </div>
    </div>
  );

  // Add a completion message when all substeps are done
  const renderCompletionMessage = () => {
    const allCompleted = substeps && substeps.every(substep => isSubstepCompleted(substep.id));
    
    if (allCompleted) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-800/60 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 shadow-lg w-full max-w-md text-center">
            <div className="w-16 h-16 mx-auto bg-green-900/30 text-green-400 rounded-full flex items-center justify-center mb-4 border border-green-700/50">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">All Questions Completed!</h3>
            <p className="text-gray-300 mb-6">Thank you for providing all the necessary information. You can now click the "Next" button below to proceed.</p>
            
            {storeUrl && (
              <a 
                href={storeUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 flex items-center justify-center gap-2 mb-4"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                View in Store
              </a>
            )}
          </div>
        </div>
      );
    }
    
    return null;
  };

  // Main component return
  return (
    <div className="h-full flex flex-col">
      {/* Progress Bar */} 
      {substeps && substeps.length > 0 && renderProgressBar()}
      
      {/* Question Area or Completion Message */} 
      {substeps && substeps.length > 0 ? (
        substeps.every(substep => isSubstepCompleted(substep.id)) 
          ? renderCompletionMessage() 
          : renderQuestion()
      ) : (
        <p className="text-center text-gray-400">No questions available for this stage.</p>
      )}
    </div>
  );
};

export default InformationCollectionChat; 